import type { Project } from '@/domain/types';
import { projectEndTicks } from '@/domain/selectors';
import type { AssetManager } from '@/assets/AssetManager';
import { encodeWav, type WavBitDepth } from '@/util/wav';
import { bufferToChannels, renderProject, RenderError } from './render';
import type { StretchCache } from './stretchCache';
import { loudnessMatchGainDb, measureLoudness, type LoudnessResult } from './loudness';
import { limitPeaks } from './limiter';

/**
 * Mixdown and stem export.
 *
 * Everything written here comes out of `renderProject`, which is the live
 * signal path run offline. There is no synthesised "preview" output anywhere in
 * this file: if the export is silent, the project is silent.
 */

export type ExportFormat = 'wav' | 'mp3';

export interface ExportSettings {
  format: ExportFormat;
  sampleRate: number;
  bitDepth: WavBitDepth;
  /** MP3 bitrate in kbps. Ignored for WAV. */
  mp3Bitrate: number;
  /** Export the loop region rather than the whole arrangement. */
  range: 'project' | 'loop';
  tailSec: number;
  normalise: boolean;
  /**
   * Bring the mix to this integrated loudness, in LUFS, instead of peak
   * normalising. Null leaves the level alone. Streaming services normalise to
   * about -14; broadcast to -23.
   */
  targetLufs: number | null;
  /**
   * Allow a limiter to hold the true peak ceiling while a loudness target is
   * reached. Off, a target that would clip is simply not reached.
   */
  limitOnExport: boolean;
}

export const DEFAULT_EXPORT_SETTINGS: ExportSettings = {
  format: 'wav',
  sampleRate: 48000,
  bitDepth: 24,
  mp3Bitrate: 320,
  range: 'project',
  tailSec: 2,
  normalise: false,
  targetLufs: null,
  limitOnExport: true,
};

/** Targets worth offering, and what each is for. */
export const LOUDNESS_TARGETS: { lufs: number; label: string }[] = [
  { lufs: -14, label: '-14 LUFS · streaming' },
  { lufs: -16, label: '-16 LUFS · podcast' },
  { lufs: -23, label: '-23 LUFS · broadcast' },
];

/** True peak is never allowed above this, whatever the loudness target asks for. */
export const TRUE_PEAK_CEILING_DBTP = -1;

export interface ExportedFile {
  name: string;
  blob: Blob;
  durationSec: number;
  peak: number;
  /** What the file measures, after any gain the settings asked for. */
  loudness: LoudnessResult;
  /**
   * Set when a loudness target could not be reached without going past the true
   * peak ceiling. The file is quieter than asked for, and says so rather than
   * clipping.
   */
  loudnessLimitedByPeak: boolean;
  /** Largest reduction the limiter applied, in dB. Zero when it did nothing. */
  limiterGainReductionDb: number;
}

export interface ExportProgressUpdate {
  stage: 'rendering' | 'encoding' | 'done';
  /** 0..1 across the whole job, including every stem. */
  progress: number;
  label: string;
}

function rangeOf(project: Project, settings: ExportSettings): { startTicks: number; endTicks: number } {
  if (settings.range === 'loop') {
    return { startTicks: project.transport.loopStartTicks, endTicks: project.transport.loopEndTicks };
  }
  return { startTicks: 0, endTicks: projectEndTicks(project) };
}

function peakOf(channels: readonly Float32Array[]): number {
  let peak = 0;
  for (const channel of channels) {
    for (let i = 0; i < channel.length; i++) {
      const v = Math.abs(channel[i]);
      if (v > peak) peak = v;
    }
  }
  return peak;
}

async function encode(
  channels: Float32Array[],
  sampleRate: number,
  settings: ExportSettings,
): Promise<Blob> {
  if (settings.format === 'wav') {
    return new Blob([encodeWav(channels, sampleRate, { bitDepth: settings.bitDepth }) as BlobPart], {
      type: 'audio/wav',
    });
  }
  return encodeMp3(channels, sampleRate, settings.mp3Bitrate);
}

/**
 * MP3 encoding runs in this tab through a pure-JavaScript LAME port, bundled
 * with the app. That keeps export working with no network connection, which a
 * server-side encoder would not.
 */
async function encodeMp3(channels: Float32Array[], sampleRate: number, bitrate: number): Promise<Blob> {
  const module = await import('@breezystack/lamejs');
  const lame = (module as unknown as { default?: typeof module }).default ?? module;

  const channelCount = Math.min(2, Math.max(1, channels.length));
  const encoder = new lame.Mp3Encoder(channelCount, sampleRate, bitrate);
  const frames = channels[0]?.length ?? 0;
  const blockSize = 1152;

  const left = toInt16(channels[0] ?? new Float32Array(0));
  const right = channelCount === 2 ? toInt16(channels[1] ?? channels[0]) : null;

  const parts: Uint8Array[] = [];
  for (let i = 0; i < frames; i += blockSize) {
    const l = left.subarray(i, Math.min(i + blockSize, frames));
    const chunk = right
      ? encoder.encodeBuffer(l, right.subarray(i, Math.min(i + blockSize, frames)))
      : encoder.encodeBuffer(l);
    if (chunk.length > 0) parts.push(new Uint8Array(chunk));
  }
  const tail = encoder.flush();
  if (tail.length > 0) parts.push(new Uint8Array(tail));

  return new Blob(parts as BlobPart[], { type: 'audio/mpeg' });
}

function toInt16(channel: Float32Array): Int16Array {
  const out = new Int16Array(channel.length);
  for (let i = 0; i < channel.length; i++) {
    const s = channel[i] < -1 ? -1 : channel[i] > 1 ? 1 : channel[i];
    out[i] = Math.round(s * (s < 0 ? 32768 : 32767));
  }
  return out;
}

function sanitiseFilename(name: string): string {
  return (
    name
      .replace(/[\\/:*?"<>|]/g, '-')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 80) || 'Untitled'
  );
}

export async function exportMixdown(
  project: Project,
  assets: AssetManager,
  settings: ExportSettings,
  onProgress?: (update: ExportProgressUpdate) => void,
  stretchCache?: StretchCache,
): Promise<ExportedFile> {
  const { startTicks, endTicks } = rangeOf(project, settings);
  if (endTicks <= startTicks) {
    throw new RenderError('There is nothing to export: the project is empty, or the loop region has no length.');
  }

  onProgress?.({ stage: 'rendering', progress: 0.05, label: 'Rendering mix' });
  const buffer = await renderProject(project, assets, {
    startTicks,
    endTicks,
    sampleRate: settings.sampleRate,
    tailSec: settings.tailSec,
    stretchCache,
  });

  const channels = bufferToChannels(buffer);
  const peak = peakOf(channels);

  onProgress?.({ stage: 'encoding', progress: 0.6, label: 'Measuring loudness' });
  const applied = applyLevel(channels, buffer.sampleRate, settings);

  onProgress?.({ stage: 'encoding', progress: 0.8, label: `Writing ${settings.format.toUpperCase()}` });
  const blob = await encode(channels, buffer.sampleRate, settings);
  onProgress?.({ stage: 'done', progress: 1, label: 'Export complete' });

  return {
    name: `${sanitiseFilename(project.meta.name)}.${settings.format}`,
    blob,
    durationSec: buffer.duration,
    peak,
    loudness: applied.loudness,
    loudnessLimitedByPeak: applied.limitedByPeak,
    limiterGainReductionDb: applied.gainReductionDb,
  };
}

/**
 * Applies whatever level change the settings ask for, and reports what the file
 * measures afterwards.
 *
 * A loudness target takes precedence over peak normalising, because the two
 * disagree by design: peak normalising makes the loudest sample touch the
 * ceiling, which says nothing about how loud the result sounds.
 *
 * The measurement returned is of the audio as it will be written, so what the
 * user is told is what is in the file — with the gain applied to the numbers
 * rather than by measuring twice, since a gain moves every loudness figure by
 * exactly itself.
 */
function applyLevel(
  channels: Float32Array[],
  sampleRate: number,
  settings: ExportSettings,
): { loudness: LoudnessResult; limitedByPeak: boolean; gainReductionDb: number } {
  const before = measureLoudness(channels, sampleRate);

  const scaleBy = (gainDb: number): void => {
    if (Math.abs(gainDb) < 1e-9) return;
    const scale = Math.pow(10, gainDb / 20);
    for (const channel of channels) {
      for (let i = 0; i < channel.length; i++) channel[i] *= scale;
    }
  };

  if (settings.targetLufs !== null) {
    if (!settings.limitOnExport) {
      // Without a limiter the only honest answer to "make this louder than its
      // peaks allow" is to stop where the peaks allow, and say so.
      const match = loudnessMatchGainDb(before, settings.targetLufs, TRUE_PEAK_CEILING_DBTP);
      scaleBy(match.gainDb);
      return {
        loudness: shiftLoudness(before, match.gainDb),
        limitedByPeak: match.limitedByPeak,
        gainReductionDb: 0,
      };
    }

    // With one, the gain goes on in full and the limiter holds the ceiling.
    // Limiting lowers the loudness a little — it is turning the loudest moments
    // down — so the result is measured and corrected, twice at most. Each
    // correction is smaller than the last; stopping inside a fifth of a unit is
    // far below what anyone can hear.
    let gainReductionDb = 0;
    let measured = before;
    for (let attempt = 0; attempt < 3; attempt++) {
      const wanted = settings.targetLufs - measured.integratedLufs;
      if (!Number.isFinite(wanted)) break;
      if (attempt > 0 && Math.abs(wanted) < 0.2) break;
      scaleBy(wanted);
      const limited = limitPeaks(channels, sampleRate, { ceilingDb: TRUE_PEAK_CEILING_DBTP });
      gainReductionDb = Math.max(gainReductionDb, limited.maxGainReductionDb);
      measured = measureLoudness(channels, sampleRate);
    }
    const missed = Number.isFinite(measured.integratedLufs)
      ? measured.integratedLufs < settings.targetLufs - 0.5
      : false;
    return { loudness: measured, limitedByPeak: missed, gainReductionDb };
  }

  if (settings.normalise) {
    const peak = peakOf(channels);
    const target = 0.966; // -0.3 dBFS
    if (peak > target && peak > 0) {
      const gainDb = 20 * Math.log10(target / peak);
      scaleBy(gainDb);
      return { loudness: shiftLoudness(before, gainDb), limitedByPeak: false, gainReductionDb: 0 };
    }
  }

  return { loudness: before, limitedByPeak: false, gainReductionDb: 0 };
}

/** Every loudness figure moved by a gain. A range is a difference, so it does not move. */
function shiftLoudness(loudness: LoudnessResult, gainDb: number): LoudnessResult {
  if (Math.abs(gainDb) < 1e-9) return loudness;
  return {
    integratedLufs: loudness.integratedLufs + gainDb,
    momentaryMaxLufs: loudness.momentaryMaxLufs + gainDb,
    shortTermMaxLufs: loudness.shortTermMaxLufs + gainDb,
    loudnessRangeLu: loudness.loudnessRangeLu,
    truePeakDb: loudness.truePeakDb + gainDb,
    samplePeakDb: loudness.samplePeakDb + gainDb,
  };
}

export async function exportStems(
  project: Project,
  assets: AssetManager,
  settings: ExportSettings,
  onProgress?: (update: ExportProgressUpdate) => void,
  stretchCache?: StretchCache,
): Promise<ExportedFile[]> {
  const { startTicks, endTicks } = rangeOf(project, settings);
  const trackIds = project.trackOrder.filter((id) => {
    const track = project.tracks[id];
    return track && (track.kind === 'audio' || track.kind === 'instrument') && track.clipIds.length > 0;
  });

  if (trackIds.length === 0) throw new RenderError('There are no tracks with audio to export as stems.');

  const files: ExportedFile[] = [];
  for (let i = 0; i < trackIds.length; i++) {
    const trackId = trackIds[i];
    const track = project.tracks[trackId];
    onProgress?.({
      stage: 'rendering',
      progress: i / trackIds.length,
      label: `Rendering "${track.name}" (${i + 1} of ${trackIds.length})`,
    });

    const buffer = await renderProject(project, assets, {
      startTicks,
      endTicks,
      sampleRate: settings.sampleRate,
      tailSec: settings.tailSec,
      soloTrackIds: [trackId],
      stretchCache,
    });

    const channels = bufferToChannels(buffer);
    const peak = peakOf(channels);
    // Stems are deliberately left at the level they play at: matching each one
    // to a loudness target separately would destroy the balance between them,
    // which is the only reason to export stems in the first place.
    const loudness = measureLoudness(channels, buffer.sampleRate);
    const blob = await encode(channels, buffer.sampleRate, settings);
    files.push({
      name: `${sanitiseFilename(project.meta.name)} - ${String(i + 1).padStart(2, '0')} ${sanitiseFilename(track.name)}.${settings.format}`,
      blob,
      durationSec: buffer.duration,
      peak,
      loudness,
      loudnessLimitedByPeak: false,
      limiterGainReductionDb: 0,
    });
  }

  onProgress?.({ stage: 'done', progress: 1, label: `Exported ${files.length} stems` });
  return files;
}
